export interface AppearanceSettingsInterface {
  layoutMode: 'light' | 'dark';
  scale: number | null;
  menuMode: 'static' | 'overlay';
  inlineMenuPosition: 'top' | 'bottom' | 'both';
  inputStyle: 'outlined' | 'filled';
  ripple: boolean;
  isRTL: boolean;
  theme: string;
  menuTheme: string;
  topbarTheme: string;
}

export const DEFAULT_APPEARANCE: Readonly<AppearanceSettingsInterface> = Object.freeze({
  layoutMode: 'dark', scale: null, menuMode: 'overlay', inlineMenuPosition: 'top',
  inputStyle: 'outlined', ripple: true, isRTL: false,
  theme: 'blue', menuTheme: 'dark', topbarTheme: 'dark',
});

export const COMPONENT_THEMES = [
      {name: 'indigo', color: '#2f8ee5'},
      {name: 'pink', color: '#E91E63'},
      {name: 'purple', color: '#9C27B0'},
      {name: 'deeppurple', color: '#673AB7'},
      {name: 'blue', color: '#2196F3'},
      {name: 'lightblue', color: '#03A9F4'},
      {name: 'cyan', color: '#00BCD4'},
      {name: 'teal', color: '#009688'},
      {name: 'green', color: '#4CAF50'},
      {name: 'lightgreen', color: '#8BC34A'},
      {name: 'lime', color: '#CDDC39'},
      {name: 'yellow', color: '#FFEB3B'},
      {name: 'amber', color: '#FFC107'},
      {name: 'orange', color: '#FF9800'},
      {name: 'deeporange', color: '#FF5722'},
      {name: 'brown', color: '#795548'},
      {name: 'bluegrey', color: '#607D8B'}
    ] as const;

export const MENU_THEMES = [
      {name: 'light', color: '#FDFEFF'},
      {name: 'dark', color: '#434B54'},
      {name: 'indigo', color: '#1A237E'},
      {name: 'bluegrey', color: '#37474F'},
      {name: 'brown', color: '#4E342E'},
      {name: 'cyan', color: '#006064'},
      {name: 'green', color: '#2E7D32'},
      {name: 'deeppurple', color: '#4527A0'},
      {name: 'deeporange', color: '#BF360C'},
      {name: 'pink', color: '#880E4F'},
      {name: 'purple', color: '#6A1B9A'},
      {name: 'teal', color: '#00695C'}
    ] as const;

export const TOPBAR_THEMES = [
      {name: 'lightblue', color: '#2E88FF'},
      {name: 'dark', color: '#363636'},
      {name: 'white', color: '#FDFEFF'},
      {name: 'blue', color: '#4285f4'},
      {name: 'deeppurple', color: '#4527A0'},
      {name: 'purple', color: '#6A1B9A'},
      {name: 'pink', color: '#AD1457'},
      {name: 'cyan', color: '#0097A7'},
      {name: 'teal', color: '#00796B'},
      {name: 'green', color: '#43A047'},
      {name: 'lightgreen', color: '#689F38'},
      {name: 'lime', color: '#AFB42B'},
      {name: 'yellow', color: '#FBC02D'},
      {name: 'amber', color: '#FFA000'},
      {name: 'orange', color: '#FB8C00'},
      {name: 'deeporange', color: '#D84315'},
      {name: 'brown', color: '#5D4037'},
      {name: 'grey', color: '#616161'},
      {name: 'bluegrey', color: '#546E7A'},
      {name: 'indigo', color: '#3F51B5'}
    ] as const;

/** Treat cache/API data as untrusted; only known built-in assets may be loaded. */
export function normalizeAppearance(value: unknown): AppearanceSettingsInterface {
  const result = {...DEFAULT_APPEARANCE};
  if (!value || typeof value !== 'object' || Array.isArray(value)) { return result; }
  const data = value as Record<string, unknown>;
  const choices: Record<string, readonly string[]> = {
    layoutMode: ['light', 'dark'], menuMode: ['static', 'overlay'],
    inlineMenuPosition: ['top', 'bottom', 'both'], inputStyle: ['outlined', 'filled'],
    theme: COMPONENT_THEMES.map(t => t.name), menuTheme: MENU_THEMES.map(t => t.name),
    topbarTheme: TOPBAR_THEMES.map(t => t.name),
  };
  for (const key of Object.keys(choices)) {
    if (choices[key].includes(data[key] as string)) { (result as any)[key] = data[key]; }
  }
  if (Number.isInteger(data.scale) && Number(data.scale) >= 14 && Number(data.scale) <= 18) {
    result.scale = data.scale as number;
  }
  for (const key of ['ripple', 'isRTL'] as const) {
    if (typeof data[key] === 'boolean') { result[key] = data[key] as boolean; }
  }
  return result;
}

export function appearanceScale(settings: AppearanceSettingsInterface, width: number): number {
  return settings.scale ?? (width <= 991 ? 14 : 16);
}
